import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { profileApi, referenceApi } from '@/api/endpoints'
import { Button, EnterChip } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { Combobox } from '@/components/ui/Select'
import { useEnterToContinue, useSaveStep } from '../useWizard'
import { WizardActions } from '../WizardShell'
import type { LocationPayload } from '@/types/api'

interface Props {
  onDone: () => void
  initial?: Partial<LocationPayload>
}

export function LocationStep({ onDone, initial }: Props) {
  const save = useSaveStep('location')

  const [address, setAddress] = useState(initial?.addressLine1 ?? '')
  const [city, setCity] = useState(initial?.city ?? '')
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? '')
  const [county, setCounty] = useState(initial?.countyDistrict ?? '')
  const [countryCode, setCountryCode] = useState(initial?.countryCode ?? '')
  const [stateCode, setStateCode] = useState(initial?.stateCode ?? '')

  const { data: countries = [] } = useQuery({
    queryKey: ['countries'],
    queryFn: () => referenceApi.countries(),
    staleTime: Infinity,
  })

  const { data: states = [], isFetching: loadingStates } = useQuery({
    queryKey: ['states', countryCode],
    queryFn: () => referenceApi.states(countryCode),
    enabled: Boolean(countryCode),
    staleTime: Infinity,
  })

  // A state from the previous country would be nonsense, so clear it.
  useEffect(() => {
    setStateCode('')
  }, [countryCode])

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.code,
        label: country.name,
        prefix: <span aria-hidden="true">{country.flag}</span>,
      })),
    [countries],
  )

  const stateOptions = useMemo(
    () => states.map((state) => ({ value: state.code, label: state.name })),
    [states],
  )

  const country = countries.find((c) => c.code === countryCode)
  const state = states.find((s) => s.code === stateCode)
  // Employers' forms ask for the state wherever the country has them: it is part of the address.
  const needsState = Boolean(countryCode) && !loadingStates && stateOptions.length > 0
  const canSubmit = Boolean(city.trim() && countryCode && (!needsState || stateCode))

  const submit = () => {
    if (!canSubmit || save.isPending) return
    save.mutate(
      {
        addressLine1: address || null,
        city: city.trim(),
        postalCode: postalCode || null,
        countyDistrict: county || null,
        countryCode,
        countryName: country?.name ?? countryCode,
        stateCode: stateCode || null,
        stateName: state?.name ?? null,
      },
      { onSuccess: onDone },
    )
  }

  useEnterToContinue(submit, canSubmit)
  const fieldError = (name: string) => save.error?.fieldErrors[name] ?? null

  return (
    <>
      <div className="space-y-7">
        <TextField
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          placeholder="Start typing your address..."
          autoFocus
          autoComplete="street-address"
          aria-label="Street address"
          error={fieldError('addressLine1')}
        />

        <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
          <TextField
            label="City"
            value={city}
            onChange={(event) => setCity(event.target.value)}
            placeholder="San Francisco"
            autoComplete="address-level2"
            required
            error={fieldError('city')}
          />
          <TextField
            label="ZIP"
            value={postalCode}
            onChange={(event) => setPostalCode(event.target.value)}
            placeholder="94103"
            autoComplete="postal-code"
            error={fieldError('postalCode')}
          />
        </div>

        <TextField
          label="County / District"
          value={county}
          onChange={(event) => setCounty(event.target.value)}
          placeholder="Auto-filled from your address"
          error={fieldError('countyDistrict')}
        />

        <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
          <Combobox
            label="Country"
            options={countryOptions}
            value={countryCode || null}
            onChange={setCountryCode}
            placeholder="Search country..."
            required
            error={fieldError('countryCode')}
          />
          <Combobox
            label="State"
            options={stateOptions}
            value={stateCode || null}
            onChange={setStateCode}
            placeholder={
              !countryCode
                ? 'Pick a country first'
                : loadingStates
                  ? 'Loading…'
                  : stateOptions.length === 0
                    ? 'Not applicable'
                    : 'Select a state'
            }
            disabled={!countryCode || stateOptions.length === 0}
            error={fieldError('stateCode') ?? (needsState && !stateCode && city.trim() ? 'Pick your state — employers ask for it with the address' : undefined)}
          />
        </div>
      </div>

      <WizardActions>
        <Button
          size="lg"
          className="flex-1 sm:flex-none"
          onClick={submit}
          disabled={!canSubmit}
          loading={save.isPending}
        >
          Continue
          <EnterChip />
        </Button>
      </WizardActions>
    </>
  )
}

/** Reads any location already on the profile, so returning users see it. */
export function useExistingLocation() {
  return useQuery({
    queryKey: ['profile', 'location'],
    queryFn: async () => (await profileApi.get()).location,
    staleTime: 30_000,
  })
}
